import { computed, ref } from 'vue';
import { cloneDeep, get, set, uniq } from 'lodash';
import ColumnsService from '@/services/columns';
import { GENERAL_PRESET } from '@/services/constants/report';
import { buildDefaultColumns } from '@/store/modules/report-columns-default-columns';
import { useStore } from '@/store';
import { getTableActiveReports } from '@/store/modules/table/accessors';
import { getActiveTab as getTableActiveTab } from '@/store/modules/table/active-tab';
import { getSavedColumnsForCurrentTab } from '@/store/modules/table/report-params';
import { setTableActiveReportSavedColumns } from '@/store/modules/table/query-actions';

const CUSTOM_COLUMNS_STORAGE_KEY = 'custom-columns';

const readCustomColumnsFromStorage = () =>
  JSON.parse(window.localStorage.getItem(CUSTOM_COLUMNS_STORAGE_KEY) || '[]');

export type CustomColumnItem = {
  slug: string;
  id?: string;
  name?: string;
  format?: string;
  formula?: string;
  description?: string;
  decimalPlaces?: number;
};

type SavedColumnsContextItem = {
  columns?: string[];
  width?: Record<string, unknown>;
  order?: unknown;
  context?: string;
  totalType?: Record<string, unknown>;
};
type SavedColumnsContext = Record<string, SavedColumnsContextItem>;
type SavedColumnsByPreset = Record<string, SavedColumnsContext>;
type PresetInfo = { presetId: string; presetName: string | null };
type GroupColumnIndex = { groupId: string; index: number };
type SavedGroups = {
  groups: Record<string, { groupId: string; index: number; name?: string; isOpened?: boolean }>;
  columns: Record<string, GroupColumnIndex>;
};

const omitReportColumns = (
  data: SavedColumnsContext,
  omitColumns: string[],
): SavedColumnsContext =>
  Object.entries(data).reduce<SavedColumnsContext>((accumulator, [key, value]) => {
    const columns = (value.columns ?? []).filter((column: string) => !omitColumns.includes(column));
    const width = Object.entries(value.width ?? {}).reduce<Record<string, unknown>>(
      (widthAccumulator, [widthKey, widthValue]) => {
        if (!omitColumns.includes(widthKey)) {
          widthAccumulator[widthKey] = widthValue;
        }
        return widthAccumulator;
      },
      {},
    );

    accumulator[key] = {
      ...value,
      columns,
      width,
    };
    return accumulator;
  }, {});

export const createReportColumnsModule = () => {
  const activePreset = ref<string>(window.localStorage.getItem('activePreset') || GENERAL_PRESET);
  const savedColumns = ref<SavedColumnsByPreset>({});
  const presetsInfo = ref<PresetInfo[]>([]);
  const savedGroups = ref<SavedGroups>({ groups: {}, columns: {} });
  const dimensions = ref<Record<string, unknown>>({});
  const metrics = ref<unknown[]>([]);
  const customColumnsList = ref<CustomColumnItem[]>(readCustomColumnsFromStorage());

  const setCustomColumns = (columns: CustomColumnItem[]) => {
    customColumnsList.value = columns;
    window.localStorage.setItem(CUSTOM_COLUMNS_STORAGE_KEY, JSON.stringify(columns));
  };

  const getCustomColumns = (): Record<string, CustomColumnItem> =>
    customColumnsList.value.reduce<Record<string, CustomColumnItem>>((accumulator, column) => {
      accumulator[column.slug] = column;
      return accumulator;
    }, {});

  const savedColumnsInCurentPreset = computed<SavedColumnsContext>(() => {
    return savedColumns.value[activePreset.value] ?? {};
  });

  const activePresetData = computed(() => {
    return presetsInfo.value.find(preset => preset.presetId === activePreset.value);
  });

  const setActivePreset = (presetId: string) => {
    activePreset.value = presetId;
    window.localStorage.setItem('activePreset', presetId);
  };

  const setPresetsInfo = (payload: PresetInfo[]) => {
    presetsInfo.value = payload;
  };

  const updatePreset = ({
    presetId,
    presetData,
  }: {
    presetId: string;
    presetData: SavedColumnsContext;
  }) => {
    savedColumns.value = {
      ...savedColumns.value,
      [presetId]: presetData,
    };
  };

  const setActiveReportColumns = ({
    columns,
    id,
  }: {
    columns: SavedColumnsContext;
    id?: string | number;
  }) => {
    setTableActiveReportSavedColumns({ columns, id });
  };

  const omitPresetColumns = (columnsToOmit: string[]) => {
    const store = useStore();
    const { independentColumnSettingsEnabled } = store.user;
    const { dimensions } = store;
    const currentSavedColumns = savedColumnsInCurentPreset.value;

    let contexts = ColumnsService.type ? [ColumnsService.type] : Object.keys(dimensions);

    if (ColumnsService.type && !independentColumnSettingsEnabled) {
      contexts = uniq([...contexts, ...Object.keys(dimensions)]);
    }

    const preparedColumnSettings = contexts.reduce<SavedColumnsContext>((accumulator, context) => {
      const contextColumns = currentSavedColumns[context]?.columns ?? [];
      const contextWidth = currentSavedColumns[context]?.width ?? {};
      accumulator[context] = {
        ...currentSavedColumns[context],
        columns: contextColumns.filter((column) => !columnsToOmit.includes(column)),
        width: Object.entries(contextWidth).reduce<Record<string, unknown>>(
          (widthAccumulator, [widthKey, widthValue]) => {
            if (!columnsToOmit.includes(widthKey)) {
              widthAccumulator[widthKey] = widthValue;
            }
            return widthAccumulator;
          },
          {},
        ),
      };
      return accumulator;
    }, cloneDeep(currentSavedColumns));

    updatePreset({
      presetId: activePreset.value,
      presetData: preparedColumnSettings,
    });
  };

  const omitReportsColumns = (columnsToOmit: string[]) => {
    const preparedColumnSettings = omitReportColumns(savedColumnsInCurentPreset.value, columnsToOmit);
    updatePreset({
      presetId: activePreset.value,
      presetData: preparedColumnSettings,
    });

    getTableActiveReports().forEach((report) => {
      const reportSavedColumns = report.state?.savedColumns ?? {};
      const columns = omitReportColumns(reportSavedColumns, columnsToOmit);
      setActiveReportColumns({ columns, id: report.tabId });
    });
  };

  const setDimensions = (value: Record<string, unknown>) => {
    dimensions.value = value;
  };

  const setMetrics = (value: unknown[]) => {
    metrics.value = value;
  };

  const getDefaultColumns = (): SavedColumnsContext => {
    return buildDefaultColumns(dimensions.value, metrics.value) as SavedColumnsContext;
  };

  const serverFormatColumnsToLocal = (
    data: Array<{
      context: string;
      order: unknown;
      columns: Array<{ name: string; width: unknown; totalType?: unknown }>;
    }>,
  ): SavedColumnsContext => {
    return data.reduce<SavedColumnsContext>((accumulator, contextInstance) => {
      const { context, order } = contextInstance;
      const normalizedColumns = contextInstance.columns.reduce<{
        width: Record<string, unknown>;
        columns: string[];
        totalType: Record<string, unknown>;
      }>(
        (columnsAccumulator, columnInstance) => {
          columnsAccumulator.columns.push(columnInstance.name);
          columnsAccumulator.width[columnInstance.name] = columnInstance.width;
          if (columnInstance.totalType) {
            columnsAccumulator.totalType[columnInstance.name] = columnInstance.totalType;
          }
          return columnsAccumulator;
        },
        { columns: [], width: {}, totalType: {} },
      );

      accumulator[context] = {
        width: normalizedColumns.width,
        columns: normalizedColumns.columns,
        order,
        context,
        totalType: normalizedColumns.totalType,
      };

      return accumulator;
    }, { ...getDefaultColumns() });
  };

  const setSavedColumns = (
    presets: Array<{
      id?: string;
      name?: string | null;
      settings: Array<{
        context: string;
        order: unknown;
        columns: Array<{ name: string; width: unknown; totalType?: unknown }>;
      }>;
    }>,
  ) => {
    const presetsList: PresetInfo[] = [];
    const preparedColumnSettings = presets.reduce<SavedColumnsByPreset>((presetsAccumulator, preset) => {
      const presetId = preset.id || GENERAL_PRESET;
      presetsList.push({
        presetId,
        presetName: preset.name ?? null,
      });
      presetsAccumulator[presetId] = serverFormatColumnsToLocal(preset.settings);
      return presetsAccumulator;
    }, {});

    savedColumns.value = preparedColumnSettings;
    if (!savedColumns.value[activePreset.value]) {
      activePreset.value = GENERAL_PRESET;
    }

    presetsInfo.value = presetsList.filter((item: PresetInfo, index: number, array: PresetInfo[]) => {
      return array.findIndex((listItem: PresetInfo) => listItem.presetId === item.presetId) === index;
    });
  };

  const setGroupsColumns = (
    groups: Array<{ name?: string; isOpened?: boolean; columns: string[]; groupId: string }>,
  ) => {
    const preparedGroupsColumnSettings = groups.reduce<SavedGroups>(
      (accumulator, groupInstance, groupIndex) => {
        accumulator.groups[groupInstance.groupId] = {
          groupId: groupInstance.groupId,
          index: groupIndex,
          name: groupInstance.name,
          isOpened: groupInstance.isOpened,
        };
        groupInstance.columns.forEach((column: string, columnIndex: number) => {
          accumulator.columns[column] = {
            groupId: groupInstance.groupId,
            index: columnIndex,
          };
        });
        return accumulator;
      },
      { groups: {}, columns: {} },
    );
    savedGroups.value = preparedGroupsColumnSettings;
  };

  const deletePreset = ({ presetId }: { presetId: string }) => {
    const newSavedColumns = { ...savedColumns.value };
    delete newSavedColumns[presetId];
    savedColumns.value = newSavedColumns;
  };

  const patchSavedColumnsFromServerFormat = ({
    presetId,
    presetDataServerFormat,
  }: {
    presetId: string;
    presetDataServerFormat: Array<{
      context: string;
      order: unknown;
      columns: Array<{ name: string; width: unknown; totalType?: unknown }>;
    }>;
  }) => {
    updatePreset({
      presetId,
      presetData: {
        ...savedColumnsInCurentPreset.value,
        ...serverFormatColumnsToLocal(presetDataServerFormat),
      },
    });
  };

  const patchWidth = ({
    contexts,
    columnName,
    width,
  }: {
    contexts: string[];
    columnName: string;
    width: number;
  }) => {
    const currentSavedColumns = savedColumnsInCurentPreset.value;
    const preparedColumnSettings = contexts.reduce<SavedColumnsContext>((accumulator, context) => {
      accumulator[context] = set({ ...currentSavedColumns[context] }, ['width', columnName], width);
      return accumulator;
    }, cloneDeep(currentSavedColumns));

    updatePreset({
      presetId: activePreset.value,
      presetData: preparedColumnSettings,
    });
  };

  const patchOrder = (order: { orderBy: string; orderDir: string }) => {
    const store = useStore();
    const { independentColumnSettingsEnabled } = store.user;
    const { dimensions } = store;
    const currentSavedColumns = savedColumnsInCurentPreset.value;

    const getPreparedColumns = (sourceSavedColumns: SavedColumnsContext) => {
      let contexts = [ColumnsService.type];

      if (!independentColumnSettingsEnabled) {
        contexts = uniq([
          ...contexts,
          ...Object.keys(dimensions).filter((key) =>
            get(sourceSavedColumns, [key, 'columns'], [] as string[]).includes(order.orderBy),
          ),
        ]);
      }

      return contexts.reduce<SavedColumnsContext>((accumulator, context) => {
        accumulator[context] = {
          ...sourceSavedColumns[context],
          order,
        };
        return accumulator;
      }, cloneDeep(sourceSavedColumns));
    };

    if (getTableActiveTab()) {
      setActiveReportColumns({ columns: getPreparedColumns(getSavedColumnsForCurrentTab()) });
      return;
    }

    updatePreset({
      presetId: activePreset.value,
      presetData: getPreparedColumns(currentSavedColumns),
    });
  };

  const applyActiveReportColumnsFromContexts = (
    contexts: Array<{
      context: string;
      order: unknown;
      columns: Array<{ name: string; width: unknown; totalType?: unknown }>;
    }>,
  ) => {
    const preparedColumnSettings = contexts.reduce<SavedColumnsContext>((accumulator, column) => {
      const { columns, width, totalType } = column.columns.reduce<{
        columns: string[];
        width: Record<string, unknown>;
        totalType: Record<string, unknown>;
      }>(
        (columnsAccumulator, columnInstance) => {
          columnsAccumulator.columns.push(columnInstance.name);
          columnsAccumulator.width[columnInstance.name] = columnInstance.width;
          if (columnInstance.totalType) {
            columnsAccumulator.totalType[columnInstance.name] = columnInstance.totalType;
          }
          return columnsAccumulator;
        },
        { columns: [], width: {}, totalType: {} },
      );

      accumulator[column.context] = {
        ...column,
        columns,
        width,
        totalType,
      };

      return accumulator;
    }, cloneDeep(getDefaultColumns()));

    setActiveReportColumns({ columns: preparedColumnSettings });
  };

  const getSupportedMetricsAsObject = () => {
    const result: Record<string, boolean> = {};
    metrics.value.forEach((column) => {
      result[column as string] = true;
    });
    return result;
  };

  const reset = () => {
    activePreset.value = GENERAL_PRESET;
    savedColumns.value = {};
    presetsInfo.value = [];
    savedGroups.value = { groups: {}, columns: {} };
    dimensions.value = {};
    metrics.value = [];
    customColumnsList.value = [];
  };

  return {
    state: {
      activePreset,
      savedColumns,
      presetsInfo,
      savedGroups,
      dimensions,
      metrics,
      savedColumnsInCurentPreset,
      activePresetData,
      setDimensions,
      setMetrics,
      getDefaultColumns,
      setSavedColumns,
      setGroupsColumns,
      setActivePreset,
      setPresetsInfo,
      updatePreset,
      setActiveReportColumns,
      omitPresetColumns,
      omitReportsColumns,
      deletePreset,
      patchSavedColumnsFromServerFormat,
      patchWidth,
      patchOrder,
      applyActiveReportColumnsFromContexts,
      getSupportedMetricsAsObject,
      customColumnsList,
      setCustomColumns,
      getCustomColumns,
    },
    reset,
  };
};
